/**
 * Plugin de Observabilidade — captura erros e dificuldades dos usuários
 * e envia automaticamente para /api/dkbackend/logs.
 */

const ENDPOINT = `${process.env.VUE_APP_HOST_API_BACKEND || ''}/logs`

let _sessionId = null
function getSessionId() {
  if (!_sessionId) {
    _sessionId = sessionStorage.getItem('obs_sid')
    if (!_sessionId) {
      _sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36)
      sessionStorage.setItem('obs_sid', _sessionId)
    }
  }
  return _sessionId
}

function getUserData() {
  try {
    const raw = localStorage.getItem('userData')
    if (!raw) return {}
    const u = JSON.parse(raw)
    return { user_id: u.id || null, user_email: u.email || null }
  } catch { return {} }
}

let _queue = []
let _flushTimer = null

function enqueue(payload) {
  _queue.push(payload)
  if (!_flushTimer) {
    _flushTimer = setTimeout(flush, 2000)
  }
}

function flush() {
  _flushTimer = null
  if (!_queue.length) return
  const batch = _queue.splice(0)
  batch.forEach(send)
}

function send(payload) {
  try {
    const user = getUserData()
    const body = JSON.stringify({
      ...payload,
      ...user,
      session_id: getSessionId(),
      page_url: window.location.href,
      user_agent: navigator.userAgent,
    })
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon(ENDPOINT, blob)
    } else {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {})
    }
  } catch (e) {
    console.warn('[obs] falha ao enviar log:', e)
  }
}

export function logError(message, opts = {}) {
  enqueue({ level: 'error', ...opts, message: String(message).slice(0, 999) })
}

export function logWarn(message, opts = {}) {
  enqueue({ level: 'warn', ...opts, message: String(message).slice(0, 999) })
}

export function logInfo(message, opts = {}) {
  enqueue({ level: 'info', ...opts, message: String(message).slice(0, 999) })
}

const ObservabilityPlugin = {
  install(Vue) {
    /* ── 1. Erros Vue (componentes) ──────────────────────── */
    const prevHandler = Vue.config.errorHandler
    Vue.config.errorHandler = (err, vm, info) => {
      const component = vm?.$options?.name || vm?.$options?.__file || 'unknown'
      enqueue({
        level: 'error',
        category: 'ui',
        message: err?.message || String(err),
        stack: err?.stack,
        details: { component, info, hook: info },
      })
      if (prevHandler) prevHandler(err, vm, info)
      else console.error('[Vue error]', err)
    }

    /* ── 2. Erros globais JS (fora do Vue) ───────────────── */
    window.addEventListener('error', (event) => {
      if (!event.error) return
      enqueue({
        level: 'error',
        category: 'ui',
        message: event.message || String(event.error),
        stack: event.error?.stack,
        details: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      })
    })

    /* ── 3. Promises rejeitadas não tratadas ─────────────── */
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason
      const message = reason?.message || String(reason) || 'Unhandled promise rejection'
      enqueue({
        level: 'error',
        category: 'promise',
        message,
        stack: reason?.stack,
        details: { type: 'unhandledrejection' },
      })
    })

    /* ── 4. Erros de recurso (imagens, scripts, etc.) ────── */
    window.addEventListener('error', (event) => {
      const target = event.target
      if (!target || target === window) return
      const tag = target.tagName?.toLowerCase()
      if (!['img', 'script', 'link', 'iframe'].includes(tag)) return
      const src = target.src || target.href || ''
      // Ignora pixels de tracking e URLs que não são recursos reais
      const ignorePatterns = [
        'facebook.com', 'fbclid=', 'utm_source=', 'googletagmanager.com',
        'google-analytics.com', 'clarity.ms', 'doubleclick.net', 'fbevents.js',
        'connect.facebook.net', 'analytics.js', 'gtag/js',
      ]
      if (ignorePatterns.some(p => src.includes(p))) return
      // Ignora 1x1 tracking pixels (imagens sem dimensão)
      if (tag === 'img' && target.width <= 1 && target.height <= 1) return
      // Ignora imgs cujo src não é imagem real (vídeos, páginas, tracking)
      if (tag === 'img') {
        if (src.match(/\.(mp4|webm|m3u8|ts|m4s|mov|avi)(\?|$)/i)) return
        try {
          const srcUrl = new URL(src, window.location.origin)
          if (srcUrl.origin === window.location.origin && !srcUrl.pathname.match(/\.(png|jpe?g|gif|svg|webp|ico|avif)$/i)) return
        } catch {}
      }
      enqueue({
        level: 'warn',
        category: 'resource',
        message: `Falha ao carregar ${tag}: ${src}`.slice(0, 999),
        details: { tag, src },
      })
    }, true)

    /* ── 5. Métodos globais no Vue ───────────────────────── */
    Vue.prototype.$logError = (msg, opts) => logError(msg, opts)
    Vue.prototype.$logWarn  = (msg, opts) => logWarn(msg, opts)
    Vue.prototype.$logInfo  = (msg, opts) => logInfo(msg, opts)

    /* ── 6. Axios interceptor — erros de API ─────────────── */
    Vue.nextTick(() => {
      const http = Vue.prototype.$http
      if (http && http.interceptors) {
        http.interceptors.response.use(
          (res) => res,
          (error) => {
            const status = error?.response?.status
            const url = error?.config?.url || ''
            // Ignora o próprio endpoint de logs para evitar recursão
            if (url.includes('/logs')) return Promise.reject(error)
            // Ignora 401/403 (são fluxos normais de auth)
            if (status === 401 || status === 403) return Promise.reject(error)
            const message = `API ${status || 'Network'}: ${error?.response?.data?.message || error.message || url}`.slice(0, 999)
            enqueue({
              level: status >= 500 ? 'error' : 'warn',
              category: 'api',
              message,
              details: {
                url,
                method: error?.config?.method?.toUpperCase(),
                status,
                response: JSON.stringify(error?.response?.data || {}).slice(0, 500),
              },
            })
            return Promise.reject(error)
          },
        )
      }
    })

    /* ── 7. Flush antes de sair ──────────────────────────── */
    window.addEventListener('beforeunload', flush)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush()
    })
  },
}

export default ObservabilityPlugin
